/* eslint-disable @typescript-eslint/no-shadow */
import { Block, BlockResult, Library, LibraryState } from "blocks/lib/types";
import { v4 as uuidv4 } from "uuid";
import { createContainer } from "unstated-next";
import React, { useCallback, useEffect, useState } from "react";
import { fitBlocksToRegion } from "lib/isp-canvas/utils";
import { BlockType } from "./renderer";
import { getters } from "../../blocks/lib/constants";
import { useHistory, useParams } from "react-router-dom";
import { getAuthHeader } from "../auth";
import axios from "axios";
import config from "../../config";
import {
  findBlock,
  computeMetrics,
  applyBlocks,
} from "../../blocks/lib/blocks";
import {
  cloneBlock,
  convertArrayToMatrix,
  convertMatrixToArray,
} from "../../blocks/lib/util";
import { useBlockLibrariesCtrl } from "./block-libraries";

const defaultBlockJson = `{"id": "06e8c5f4-d0a3-449e-ba17-0255b7955072","props": {"name": "06e8c5f4-d0a3-449e-ba17-0255b7955072","layout": {"mode": "flex","flex": {"mode": "vertical","primarySizing": "auto","crossSizing": "auto","primaryAlign": "min","crossAlign": "min","spacing": 0,"repeat": [0,0],"rotation": 0},"grid": {"horizontalSpacing": [0],"verticalSpacing": [0],"horizontalMirror": false,"verticalMirror": false,"repeat": [[0,0],[0,0]]},"padding": [0,0,0,0],"offset": [0,0,0,0],"rotation": 0},"size": {"size": [428,276.0000305175781],"range": [[428,428],[276.0000305175781,276.0000305175781]]},"metrics": {"size": [0,0],"seats": 0,"areaRange": [0,0],"area": 0,"names": {},"tags": {},"types": {},"seatsRange": [0,0],"sizeRange": [[144,360],[114,114]],"costUnit": 0, "costGSF": 0},"definition": {"category": "","subcategory": "","type": "","subtype": "","name": "Desks","description": "","library": "","url": "","tags": []}},"children": [{"id": "Frame7","props": {"name": "Frame7","layout": {"mode": "flex","flex": {"mode": "horizontal","primarySizing": "auto","crossSizing": "auto","primaryAlign": "center","crossAlign": "min","spacing": 0,"repeat": [2,5],"rotation": -90},"grid": {"horizontalSpacing": [0],"verticalSpacing": [0],"horizontalMirror": false,"verticalMirror": false,"repeat": [[0,0],[0,0]]},"padding": [0,0,0,0],"offset": [0,0,0,0],"rotation": 0},"size": {"size": [184,85.00000762939453],"range": [[184,184],[85.00000762939453,85.00000762939453]]},"metrics": {"area": 8208,"areaRange": [8208,20520],"seatsRange": [0,0],"seats": 0,"tags": {},"names": {"": 1},"types": {"Desk": {"area": 8208,"areaRange": [8208,20520],"seats": 0,"seatsRange": [0,0],"tags": {},"names": {"": 1}}},"sizeRange": [[144,360],[57,57]]}},"children": [],"geometry": {"type": "box","box": [[0,0],[184,85.00000762939453]]},"role": "block","symbol": "Asset-Desk-Small","matrix": [1,0,0,0,0,1,0,0,0,0,1,0,0,0,0,1]},{"id": "Frame8","props": {"name": "Frame8","layout": {"mode": "flex","flex": {"mode": "horizontal","primarySizing": "auto","crossSizing": "auto","primaryAlign": "center","crossAlign": "min","spacing": 0,"repeat": [2,5],"rotation": 90},"grid": {"horizontalSpacing": [0],"verticalSpacing": [0],"horizontalMirror": false,"verticalMirror": false,"repeat": [[0,0],[0,0]]},"padding": [0,0,0,0],"offset": [0,0,0,0],"rotation": 180},"size": {"size": [184,85.00000762939453],"range": [[184,184],[85.00000762939453,85.00000762939453]]},"metrics": {"area": 8208,"areaRange": [8208,20520],"seatsRange": [0,0],"seats": 0,"tags": {},"names": {"": 1},"types": {"Desk": {"area": 8208,"areaRange": [8208,20520],"seats": 0,"seatsRange": [0,0],"tags": {},"names": {"": 1}}},"sizeRange": [[144,360],[57,57]]}},"children": [],"geometry": {"type": "box","box": [[0,0],[184,85.00000762939453]]},"role": "block","symbol": "Asset-Desk-Small","matrix": [1,0,0,0,0,1,0,0,0,0,1,0,0,191,0,1]}],"geometry": {"type": "box","box": [[0,0],[428,276.0000305175781]]},"role": "block","matrix": [1,0,0,0,0,1,0,0,0,0,1,0,0,0,0,1]}`;

export interface UseBlocksType {
  stack: Block[];
  stackIndex: number;
  blocks: Block[];
  filterBlocksByLibrary: (
    blockResults: BlockResult[],
    activeLibraries: string[]
  ) => BlockResult[];
  blockLibrary: string;
  defaultFocusBlockID: string;
  saveBlockLibrary: (library: string) => void;
  blockClassifications: { [key: string]: BlockType };
  activeBlock?: Block;
  activeBMInstance?: Block;
  activeSubBlock: string;
  blockLibraries: { title: string; value: string; state: LibraryState }[];
  filteredLibraries: string[];
  setFilteredLibraries(filteredLibraries: string[]): void;
  newBlock(): Promise<Block>;
  saveBlock(t: Block): Promise<Block>;
  createBlock(t: Block): Promise<Block>;
  createOrUpdateBlock(t: Block): Promise<Block>;
  importBlocks(b: Block[]): Promise<void>;
  deleteBlock(id: string): Promise<void>;
  copyBlock(id: string): Promise<Block>;
  setBlocks(blocks: Block[]): void;
  setSubBlockSymbol(id: string): void;
  undo(): void;
  redo(): void;
  setActiveSubBlock(t: string): void;
  setActiveBMInstance(b: Block): void;
  activateBlock(t: Block | undefined): void;
  isValidDimension(w: number, h: number, t: BlockType): boolean;
  getBlockById(id: string | undefined): Block | undefined;
  getBlocksBySize(width: number, height: number): BlockResult[];
  getBlocksBySizeAndType(
    width: number,
    height: number,
    typeSelection: BlockType
  ): BlockResult[];
  getBlockLibraries(): void;
  setLibrary(id: string, library: string): Promise<void>;
  removeLibrary(id: string): Promise<void>;
  isBlockValidDimension(w: number, h: number, block: Block): boolean;
}

interface GetBlockResponse {
  settings: { defaultBlockId: string };
  blocks: Block[];
}

const deleteLocalStorage = (id: string) => {
  const has = localStorage.getItem("blocks");
  let blocks: Block[] = [];
  if (has) {
    blocks = JSON.parse(has);
    const idx = blocks.findIndex((b: Block) => b.id === id);
    if (idx >= 0) {
      blocks.splice(idx, 1);
    }
  }
  localStorage.setItem("blocks", JSON.stringify(blocks));
};
const updateLocalStorage = (block: Block) => {
  const has = localStorage.getItem("blocks");
  let blocks = [block];
  if (has) {
    blocks = JSON.parse(has);
    const idx = blocks.findIndex((b: Block) => b.id === block.id);
    if (idx >= 0) {
      blocks.splice(idx, 1, block);
    } else {
      blocks.push(block);
    }
  }
  localStorage.setItem("blocks", JSON.stringify(blocks));
};

const maxStackLength = 10;

type BlockLibraries = { title: string; value: string; state: LibraryState }[];

const useBlocksContainer = (): UseBlocksType => {
  const [stack, setStack] = useState<Block[]>([]);
  const [stackIndex, setStackIndex] = useState<number>(0);
  let cachedBlocks = localStorage.getItem("blocks");
  if (!cachedBlocks || cachedBlocks === "undefined") cachedBlocks = "[]";
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const [blocks, setBlocks] = useState<Block[]>(JSON.parse(cachedBlocks!));
  const [loaded, setLoaded] = useState<boolean>(false);
  const [defaultFocusBlockID, setDefaultFocusBlockID] = useState("");
  const [activeBlock, setActiveBlock] = useState<Block | undefined>(undefined);
  const [activeSubBlock, setActiveSubBlock] = useState<string>("");
  const [activeBMInstance, setActiveBMInstance] = React.useState<
    Block | undefined
  >();
  const [blockClassifications, setBlockClassifications] = useState<{
    [key: string]: BlockType;
  }>({});
  const [blockLibrary, setBlockLibrary] = useState<string>("all_blocks");
  const [blockLibraries, innerSetBlockLibraries] =
    React.useState<BlockLibraries>([]);
  const [filteredLibraries, setFilteredLibraries] = React.useState<string[]>(
    []
  );
  const { libraries } = useBlockLibrariesCtrl();
  const { id } = useParams<{ id: string }>();

  const setBlockLibraries = useCallback(
    (libraries: BlockLibraries) => {
      const l = libraries.slice();
      l.sort((a, b) => {
        return a.title.localeCompare(b.title);
      });
      innerSetBlockLibraries(l);
    },
    [innerSetBlockLibraries]
  );

  useEffect(() => {
    const found = blocks.find((el) => el.id === id);
    if (found && (!activeBlock || activeBlock.id !== id)) setActiveBlock(found);
  }, [id, blocks, activeBlock]);

  useEffect(() => {
    const optionMap: Record<string, Partial<Library>> = {};
    blocks.forEach((block) => {
      if (
        block.props &&
        block.props.definition &&
        block.props.definition.library
      ) {
        const found = libraries.find(
          (el) => el.id === block.props.definition?.library
        );
        if (found) {
          optionMap[block.props.definition.library] = {
            title: found.title,
            state: found.state,
          };
        }
      }
    });

    let tempLibrary: { title: string; value: string; state: LibraryState }[] =
      [];
    tempLibrary = Object.keys(optionMap).map((key) => ({
      value: key,
      title: optionMap[key].title as string,
      state: optionMap[key].state as LibraryState,
    }));

    setBlockLibraries(tempLibrary);
  }, [blocks, libraries, setBlockLibraries]);

  const history = useHistory();

  const pushStack = useCallback(
    (block: Block) => {
      setStack((thisStack) => {
        const s = thisStack.slice(0, stackIndex + 1);
        if (thisStack.length > maxStackLength) s.shift();
        s.push(cloneBlock(block));
        setStackIndex(s.length - 1);
        return s;
      });
    },
    [stackIndex]
  );

  const undo = () => {
    const idx = Math.max(stackIndex - 1, 0);
    setStackIndex(idx);
    saveBlock(cloneBlock(stack[idx]), false);
  };

  const redo = () => {
    const idx = Math.min(stackIndex + 1, stack.length - 1);
    setStackIndex(idx);
    saveBlock(cloneBlock(stack[idx]), false);
  };

  const getBlockById = useCallback(
    (id: string | undefined): Block | undefined => {
      return id ? blocks.find((t) => t.id === id) : undefined;
    },
    [blocks]
  );

  useEffect(() => {
    const getBlocks = async (): Promise<void> => {
      if (!loaded) {
        const headers = await getAuthHeader();
        const { data } = await axios.get(config.baseUrl + `/api/blocks/`, {
          headers,
        });

        const { blocks, settings } = data as GetBlockResponse;
        localStorage.setItem("blocks", JSON.stringify(blocks));
        setLoaded(true);

        let activeBlocks = blocks.filter((t) =>
          t.props.definition ? t.props.definition?.status !== "deleted" : true
        );
        activeBlocks = activeBlocks.map((el) => {
          if (el.role === "block") el = computeMetrics(el, getBlockById);
          return el;
        });

        setBlocks(activeBlocks);
        // setActiveBlock(activeBlocks[0]);
        // setStack([cloneBlock(activeBlocks[0])]);
        setStackIndex(0);
        setDefaultFocusBlockID(settings.defaultBlockId);

        const classes = classifyBlocks(blocks);
        setBlockClassifications(classes);
      }
    };

    if (!loaded) getBlocks();
  }, [
    blocks,
    loaded,
    setBlocks,
    setBlockClassifications,
    pushStack,
    getBlockById,
  ]);
  useEffect(() => {
    if (
      activeBlock &&
      ((stack.length > 0 && stack[0].id !== activeBlock.id) ||
        stack.length === 0)
    ) {
      setStack([cloneBlock(activeBlock)]);
    }
  }, [activeBlock, stack]);

  const importBlocks = async (b: Block[]): Promise<void> => {
    const headers = await getAuthHeader();
    const toCreate: Block[] = [];
    const toUpdateIdx: number[] = [];
    const toUpdate: Block[] = [];
    b.forEach((block) => {
      const idx = blocks.findIndex((el) => el.id === block.id);
      // collect all local blocks to update
      if (idx >= 0) {
        toUpdate.push(block);
        toUpdateIdx.push(idx);
      }
      // collect all new blocks to create
      else toCreate.push(block);
    });
    await Promise.all(
      toCreate.map(async (b) => {
        try {
          await axios.post(config.baseUrl + `/api/blocks/`, b, {
            headers,
            transformRequest: [
              (data) => JSON.stringify(convertMatrixToArray(data)),
            ],
          });
          blocks.push(b);
        } catch (e) {
          console.error(e);
        }
      })
    );
    await Promise.all(
      toUpdate.map(async (b, i) => {
        try {
          await axios.put(config.baseUrl + `/api/blocks/${b.id}`, b, {
            headers,
            transformRequest: [
              (data) => JSON.stringify(convertMatrixToArray(data)),
            ],
            transformResponse: [
              (data) => convertArrayToMatrix(JSON.parse(data)),
            ],
          });
          blocks[toUpdateIdx[i]] = b;
        } catch (e) {
          console.error(e);
        }
      })
    );
    setBlocks(blocks);
  };

  const createOrUpdateBlock = async (t: Block): Promise<Block> => {
    const found = blocks.find((el) => el.id === t.id);
    if (found) return saveBlock(t, false);
    else return createBlock(t);
  };

  const createBlock = async (t: Block): Promise<Block> => {
    const headers = await getAuthHeader();
    try {
      const res = await axios.post(config.baseUrl + `/api/blocks/`, t, {
        headers,
        transformRequest: [
          (data) => JSON.stringify(convertMatrixToArray(data)),
        ],
        transformResponse: [(data) => convertArrayToMatrix(JSON.parse(data))],
      });
      updateLocalStorage(res.data);
      setBlocks([...blocks, res.data]);
      return res.data;
    } catch (e) {
      console.error(e);
      return Promise.reject();
    }
  };

  const saveBlock = async (t: Block, push = true): Promise<Block> => {
    const headers = await getAuthHeader();
    try {
      const idx = blocks.findIndex((block) => block.id === t.id);
      t = computeMetrics(t, getBlockById);
      const updated = [...blocks];
      updated.splice(idx, 1, t);
      setBlocks(updated);
      updateLocalStorage(t);
      setActiveBlock(t);
      await axios.put(config.baseUrl + `/api/blocks/${t.id}/`, t, {
        headers,
        transformRequest: [
          (data) => JSON.stringify(convertMatrixToArray(data)),
        ],
        transformResponse: [(data) => convertArrayToMatrix(JSON.parse(data))],
      });
      if (push) pushStack(t);
      return t;
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const setSubBlockSymbol = (id: string) => {
    const newActiveBlock = blocks.find((t: Block) => t.id === id);
    const ref = getBlockById(id);
    const definition = ref ? getters.getDefinition(ref) : undefined;
    if (activeBlock) {
      const found = findBlock(activeBlock, activeSubBlock);
      if (found) {
        found.symbol = id;
        if (definition) {
          found.props.definition = { ...definition };
        }
        saveBlock(activeBlock);
      }
      if (!newActiveBlock) return;
    }
  };

  const newBlock = async (): Promise<Block> => {
    const newBlock = JSON.parse(defaultBlockJson);
    newBlock.id = uuidv4();
    newBlock.props.name = newBlock.id;
    computeMetrics(newBlock, getBlockById);

    const created = await createBlock(newBlock);
    history.push("/blocks/" + created.id);
    return newBlock;
  };

  const deleteBlock = async (id: string): Promise<void> => {
    const headers = await getAuthHeader();
    try {
      const idx = blocks.findIndex((block) => block.id === id);
      const updated = [...blocks];
      updated.splice(idx, 1);
      deleteLocalStorage(id);
      setBlocks(updated);
      await axios.delete(config.baseUrl + `/api/blocks/${id}/`, {
        headers,
      });
    } catch (e) {
      console.error(e);
    }
  };

  const setLibrary = async (id: string, library: string): Promise<void> => {
    const headers = await getAuthHeader();
    try {
      const idx = blocks.findIndex((block) => block.id === id);
      if (idx >= 0) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        blocks[idx].props.definition!.library = library;
        const updated = [...blocks];
        setBlocks(updated);
        updateLocalStorage(blocks[idx]);
        await axios.put(
          config.baseUrl + `/api/blocks/${id}/library`,
          { library },
          {
            headers,
          }
        );
      } else return await Promise.reject();
    } catch (e) {
      console.error(e);
    }
  };

  const removeLibrary = async (id: string): Promise<void> => {
    const headers = await getAuthHeader();
    try {
      const idx = blocks.findIndex((block) => block.id === id);
      if (idx >= 0) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        blocks[idx].props.definition!.library = "";
        const updated = [...blocks];
        setBlocks(updated);
        updateLocalStorage(blocks[idx]);
        await axios.delete(config.baseUrl + `/api/blocks/${id}/library`, {
          headers,
        });
      } else return await Promise.reject();
    } catch (e) {
      console.error(e);
    }
  };

  const copyBlock = async (id: string): Promise<Block> => {
    const found = blocks.find((block) => block.id === id);
    if (found) {
      // let copy = { ...found };
      const copy = cloneBlock(found);
      copy.id = uuidv4();
      const def = getters.getDefinition(copy);
      def.name = "Copy of " + def.name;
      copy.props.definition = def;
      await createBlock(copy);
      return copy;
    }
    return Promise.reject();
  };

  const activateBlock = (active: Block | undefined) => {
    if (!active) {
      setActiveBlock(undefined);
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const found = blocks.find((t) => t.id === active!.id);
    if (found) {
      setActiveBlock(active);
    }
  };

  const isValidDimension = (w: number, h: number): boolean => {
    const fittedBlocks = fitBlocksToRegion(
      w,
      h,
      blocks.filter((b) => b.role === "block")
    );
    return fittedBlocks.length > 0;
  };

  const isBlockValidDimension = (
    w: number,
    h: number,
    block: Block
  ): boolean => {
    const fittedBlocks = fitBlocksToRegion(w, h, [block]);
    return fittedBlocks.length > 0;
  };

  const getBlocksBySize = (width: number, height: number): BlockResult[] => {
    return fitBlocksToRegion(width, height, blocks);
  };

  const getBlocksBySizeAndType = (
    width: number,
    height: number
  ): BlockResult[] => {
    return fitBlocksToRegion(
      width,
      height,
      blocks.filter((b) => {
        const blockLib = b.props.definition?.library;
        const lib = libraries.find((library) => library.id === blockLib);
        // filters by block (as opposed to asset), in a published library,
        // and has a library (is not uncategorized)
        return b.role === "block" && lib?.state !== "unpublished" && blockLib;
      })
    );
  };

  const classifyBlocks = (blocks: Block[]): { [key: string]: BlockType } => {
    return blocks.reduce<{ [key: string]: BlockType }>((accumulator, block) => {
      let hasCollaboration = false;
      let hasFocus = false;
      applyBlocks(block, (b) => {
        if (b.symbol) {
          const ref = blocks.find((block) => block.id === b.symbol);
          if (ref) {
            if (ref.props.definition?.type === "focus") hasFocus = true;
            else if (ref.props.definition?.type === "collaboration")
              hasCollaboration = true;
          }
        }
      });
      let finalType: BlockType = BlockType.All;
      if (hasFocus && !hasCollaboration) finalType = BlockType.Focus;
      else if (!hasFocus && hasCollaboration)
        finalType = BlockType.Collaboration;
      else if (hasFocus && hasCollaboration) finalType = BlockType.MixedUse;
      accumulator[block.id] = finalType;
      return accumulator;
    }, {});
  };

  const getBlockLibraries = (): string[] => {
    const allLibraries: string[] = [];
    blocks.forEach((block) => {
      const { library } = getters.getDefinition(block);
      if (!allLibraries.includes(library)) {
        allLibraries.push(library);
      }
    });
    return allLibraries;
  };

  const filterBlocksByLibrary = (
    blockResults: BlockResult[],
    activeLibraries: string[]
  ): BlockResult[] => {
    return blockResults.filter((blockResult) => {
      const block = getBlockById(blockResult.id);

      return activeLibraries.find(
        (library) => library === block?.props.definition?.library
      );
    });
  };

  const saveBlockLibrary = (library: string) => {
    setBlockLibrary(library);
    return;
  };

  return {
    blocks,
    stack,
    stackIndex,
    blockLibrary,
    blockLibraries,
    filteredLibraries,
    setFilteredLibraries,
    filterBlocksByLibrary,
    saveBlockLibrary,
    defaultFocusBlockID,
    blockClassifications,
    activeBlock,
    activeBMInstance,
    newBlock,
    saveBlock,
    createBlock,
    createOrUpdateBlock,
    importBlocks,
    deleteBlock,
    copyBlock,
    activateBlock,
    isValidDimension,
    getBlockById,
    getBlocksBySize,
    getBlocksBySizeAndType,
    isBlockValidDimension,
    getBlockLibraries,
    setLibrary,
    removeLibrary,
    undo,
    redo,
    setSubBlockSymbol,
    activeSubBlock,
    setActiveSubBlock,
    setActiveBMInstance,
    setBlocks,
  };
};

export const BlocksController = createContainer(useBlocksContainer);
export const BlocksProvider = BlocksController.Provider;
export const useBlocksCtrl = () => BlocksController.useContainer();
